"use client";

import {createContext, useCallback, useContext, useEffect, useMemo} from "react";
import {createClient} from "~/utils/supabase/client";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {AccountResponse} from "@docid/api/src/types/accounts";
import {useClientAuth} from "~/components/providers/client/AuthProvider";
import {fetchById as fetchAccountById} from "@docid/api/src/models/accounts";

export const dynamic = "force-dynamic";

interface ClientAccountContextProps {
    readonly account: AccountResponse | undefined;
    readonly isAccountLoading: boolean;
    readonly isAccountFetching: boolean;
    readonly invalidateAccount: () => Promise<void>,
}

const ClientAccountContext = createContext<ClientAccountContextProps>({
    account: undefined,
    isAccountLoading: false,
    isAccountFetching: false,
    invalidateAccount: async() => undefined
});

export const ClientAccountProvider = ({ children } : { children: React.ReactNode }) => {

    const { isLoggedIn, isUserReady, user } = useClientAuth();

    const accountId = isLoggedIn && isUserReady ? user?.id : undefined;
    const queryClient = useQueryClient();
    const supabase = createClient();

    const {
        data,
        error,
        isLoading: isAccountLoading,
        isFetching: isAccountFetching,
        refetch,
    } = useQuery<AccountResponse | null, Error>({
        queryKey: ["accounts.user", accountId],
        queryFn: async () => {
            if (!accountId) return null;
            const {data, error} = await fetchAccountById({id: accountId}, supabase as any);
            if (error) throw new Error(error.message);
            return data;
        },
        enabled: Boolean(accountId),
        staleTime: 1000 * 60 * 5,
    });

    const account = accountId && data?.id === accountId ? data : undefined;

    useEffect(() => {
        queryClient.removeQueries({
            queryKey: ["accounts.user"],
            predicate: query => query.queryKey[1] !== accountId,
        });
    }, [accountId, queryClient]);

    useEffect(() => {
        if (error) {
            console.error("Failed to fetch user account:", error.message);
        }
    }, [error]);

    const invalidateAccount = useCallback(async() => {
        if (accountId) await refetch();
    }, [accountId, refetch]);

    const value = useMemo(() => {
        return {
            account,
            isAccountLoading,
            isAccountFetching,
            invalidateAccount,
        };
    }, [account, isAccountLoading, isAccountFetching, invalidateAccount]);

    return (
        <ClientAccountContext.Provider value={value}>
            {children}
        </ClientAccountContext.Provider>
    );

}

export const useClientAccount = () => {
    const context = useContext(ClientAccountContext);
    if (context === undefined) {
        throw new Error('useClientAccount must be used within an ClientAccountProvider.');
    }
    return context;
};
